import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { api, formatApiErrorDetail } from "@/lib/api";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = (params.get("token") || "").trim();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (password !== confirm) {
      setError("Passwords don't match");
      return;
    }
    setBusy(true);
    try {
      await api.post("/auth/reset-password", { token, password });
      setDone(true);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="min-h-screen bg-[#050505] text-white flex items-center justify-center px-4"
      data-testid="reset-password-page"
    >
      <motion.div
        className="w-full max-w-md border border-white/10 bg-[#0A0A0A] p-8"
        initial={{ y: 30, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ type: "spring", stiffness: 260, damping: 26 }}
      >
        <p className="font-mono-imn text-xs tracking-[0.3em] text-gold uppercase mb-2">
          Account recovery
        </p>
        <h1 className="font-display text-4xl font-bold uppercase tracking-tight mb-6">
          Reset Password
        </h1>

        {!token ? (
          <div data-testid="reset-no-token">
            <p className="text-sm text-red-400 mb-6">
              This reset link is missing its token. Please request a new one from the sign-in
              screen.
            </p>
            <Link
              to="/"
              data-testid="reset-home-link"
              className="block text-center w-full border border-white/20 py-3 text-sm uppercase tracking-wider hover:bg-white/5 transition-colors"
            >
              Back to It's My Network
            </Link>
          </div>
        ) : done ? (
          <div data-testid="reset-success">
            <p className="text-sm text-emerald-400 mb-6">
              Password updated. You can sign in with your new password now.
            </p>
            <Link
              to="/"
              data-testid="reset-signin-link"
              className="block text-center w-full bg-white text-black font-semibold py-3 text-sm uppercase tracking-wider hover:bg-white/90 transition-colors"
            >
              Go to Sign In
            </Link>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-4">
            <input
              data-testid="reset-password-input"
              type="password"
              minLength={6}
              className="w-full bg-white/5 border border-white/10 px-4 py-3 text-sm outline-none focus:border-gold transition-colors"
              placeholder="New password (min 6 characters)"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
            <input
              data-testid="reset-confirm-input"
              type="password"
              minLength={6}
              className="w-full bg-white/5 border border-white/10 px-4 py-3 text-sm outline-none focus:border-gold transition-colors"
              placeholder="Confirm new password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              required
            />
            {error && (
              <p className="text-sm text-red-400" data-testid="reset-error">
                {error}
              </p>
            )}
            <button
              type="submit"
              disabled={busy}
              data-testid="reset-submit-btn"
              className="w-full bg-white text-black font-semibold py-3 text-sm uppercase tracking-wider hover:bg-white/90 active:scale-[0.98] transition-all disabled:opacity-50"
            >
              {busy ? "Please wait…" : "Set New Password"}
            </button>
          </form>
        )}
      </motion.div>
    </div>
  );
}
